<template>
  <AppShell>
    <!-- Back -->
    <button class="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-white transition-colors mb-5" @click="goBack">
      <i class="fas fa-arrow-left"></i> Back to modules
    </button>

    <!-- Module hero -->
    <div v-if="module" class="relative overflow-hidden card mb-6">
      <div class="absolute inset-0 bg-cover bg-center opacity-20"
        :style="{ backgroundImage: `url(${module.imageUrl})` }"></div>
      <div class="absolute inset-0 bg-gradient-to-t from-surface via-surface/80 to-surface/40"></div>
      <div class="relative p-8 text-center">
        <div class="flex flex-wrap items-center justify-center gap-2 mb-4">
          <span v-if="module.isCompleted" class="badge bg-success text-black"><i class="fas fa-check"></i> Completed 🎉</span>
          <span v-if="module.isExclusive" class="badge bg-yellow-500 text-black"><i class="fas fa-crown"></i> Subscriber Only</span>
        </div>
        <h1 class="text-3xl sm:text-4xl font-extrabold text-white mb-3">{{ module.title }}</h1>
        <p class="text-ink-muted max-w-2xl mx-auto mb-5">{{ module.description }}</p>

        <div class="flex flex-wrap justify-center gap-1.5 mb-6">
          <span v-for="(tag, idx) in module.tags" :key="idx" class="chip">{{ tag }}</span>
        </div>

        <button v-if="!module.isCompleted && !module.isEnrolled" class="btn-primary px-6 py-3" @click="enrollInModule">
          Enroll Now <i class="fas fa-arrow-right text-xs"></i>
        </button>
        <button v-else-if="!module.isCompleted && module.isEnrolled" class="btn-primary px-6 py-3" @click="continueLearning">
          <i class="fas fa-play"></i> Continue Learning
        </button>
        <button v-if="module.isCompleted" class="btn-gold px-6 py-3" @click="downloadCertificate">
          <i class="fas fa-download"></i> Download Certificate
        </button>
      </div>
    </div>

    <!-- Submodules -->
    <div v-if="module" class="card p-6">
      <h2 class="text-xl font-bold text-white mb-5">Submodules</h2>
      <ul class="space-y-3">
        <li v-for="submodule in module.Submodules" :key="submodule.id" class="rounded-xl border border-line bg-surface-light overflow-hidden">
          <div class="flex justify-between items-center p-4 cursor-pointer hover:bg-surface-lighter transition-colors" @click="toggleDropdown(submodule.id)">
            <h3 class="text-base font-semibold text-white flex items-center gap-2">
              {{ submodule.title }}
              <span v-if="submodule.isExclusive" class="badge bg-yellow-500 text-black"><i class="fas fa-crown"></i> Exclusive</span>
              <i v-if="submodule.isCompleted" class="fas fa-check text-success"></i>
            </h3>
            <i :class="['fas text-ink-faint transition-transform duration-200', activeDropdown === submodule.id ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
          </div>
          <div v-if="activeDropdown === submodule.id" class="border-t border-line p-3 space-y-2">
            <div v-for="page in submodule.pages" :key="page.id"
              class="flex items-center justify-between p-3 rounded-lg bg-base-800 hover:bg-surface transition-colors">
              <div class="flex items-center gap-2.5">
                <i class="fas fa-book text-brand/70"></i>
                <p class="text-sm text-ink">{{ page.title }}</p>
              </div>
              <div v-if="module.isEnrolled" class="flex items-center gap-3">
                <i v-if="page.isCompleted" class="fas fa-check-circle text-success"></i>
                <a :href="`/modules/pages/${page.id}`" class="text-sm font-semibold text-brand hover:text-brand-light">View page</a>
              </div>
              <span v-else class="text-xs text-ink-faint"><i class="fas fa-lock mr-1"></i> Enroll to access</span>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <!-- Loading -->
    <div v-else class="card p-12 text-center text-ink-muted">
      <i class="fas fa-spinner fa-spin text-2xl mb-3 text-brand"></i>
      <p>Loading module details...</p>
    </div>
  </AppShell>
</template>

<script>
import AppShell from "@/components/ui-components/AppShell.vue";
import apiClient from "@/axiosConfig";
import toastr from "toastr";
import { isAuthenticated, redirectToLogin } from "@/utils/auth";

export default {
  name: "DetailModule",
  components: {
    AppShell,
  },
  data() {
    return {
      module: null, // Holds the fetched module details
      activeDropdown: null, // Tracks the currently open submodule
    };
  },
  methods: {
    async fetchModuleDetails() {
      try {
        const moduleId = this.$route.params.id;
        const response = await apiClient.get(`/modules/${moduleId}`);
        const baseUrl = process.env.VUE_APP_BASE_URL;

        // Update `isCompleted` status for module and submodules
        this.module = {
          ...response.data,
          imageUrl: `${baseUrl}/static/modules/banners/${response.data.id}`,
          // rating: response.data.rating || 5,
        };
      } catch (error) {
        console.error("Error fetching module details:", error);
      }
    },
    async enrollInModule() {
      // Check if user is authenticated before allowing enrollment
      if (!isAuthenticated()) {
        toastr.warning("Please login to enroll in modules", "Login Required");
        redirectToLogin(this.$router, this.$route.fullPath);
        return;
      }

      try {
        const moduleId = this.$route.params.id;
        const response = await apiClient.post(`/progress/enroll/${moduleId}`);
        console.log(response.data.message); // Logs enrollment success

        // Update module state after enrollment
        this.module.isEnrolled = true;

        // Redirect to the first page
        if (response.data.firstPageId) {
          this.$router.push(`/modules/pages/${response.data.firstPageId}`);
        }
      } catch (error) {
        toastr.error(error.response.data.message, "You need to subscribe");
      }
    },
    async continueLearning() {
      // Check if user is authenticated before allowing continue learning
      if (!isAuthenticated()) {
        toastr.warning("Please login to continue learning", "Login Required");
        redirectToLogin(this.$router, this.$route.fullPath);
        return;
      }

      try {
        const moduleId = this.$route.params.id;
        const response = await apiClient.get(`/progress/continue/${moduleId}`);
        console.log(response.data.message); // Logs continue message

        // Redirect to the first incomplete page
        if (response.data.firstPageId) {
          this.$router.push(`/modules/pages/${response.data.firstPageId}`);
        }
      } catch (error) {
        console.error("Error continuing learning:", error);
      }
    },
    toggleDropdown(submoduleId) {
      if (this.activeDropdown === submoduleId) {
        this.activeDropdown = null; // Close dropdown if already open
        return;
      }

      const submodule = this.module.Submodules.find((sm) => sm.id === submoduleId);
      if (submodule && submodule.pages) {
        this.activeDropdown = submoduleId; // Use cached pages if available
      } else {
        this.fetchSubmodulePages(submoduleId);
      }
    },
    async fetchSubmodulePages(submoduleId) {
      try {
        const response = await apiClient.get(`/submodules/${submoduleId}`);
        const submodule = this.module.Submodules.find((sm) => sm.id === submoduleId);
        if (submodule) {
          submodule.pages = response.data.Pages || [];
        }
        this.activeDropdown = submoduleId; // Open dropdown after fetching
      } catch (error) {
        console.error("Error fetching submodule pages:", error);
      }
    },
    async downloadCertificate() {
      // Check if user is authenticated before allowing certificate download
      if (!isAuthenticated()) {
        toastr.warning("Please login to download certificates", "Login Required");
        redirectToLogin(this.$router, this.$route.fullPath);
        return;
      }

      try {
        const moduleId = this.$route.params.id;

        // Send request to generate the certificate
        const response = await apiClient.post("/certificate", { moduleId });

        // Extract the certificate path and filename
        const { certificatePath } = response.data;
        const username = this.module.username || "user";
        const titleName = this.module.title.replace(/\s+/g, "_").toLowerCase();
        const fileName = `certificate_${username}_${titleName}.png`;

        // Open the certificate in a new tab
        const newTab = window.open(`${process.env.VUE_APP_BASE_URL}${certificatePath}`, '_blank');
        if (newTab) {
          newTab.document.title = fileName; // Optional: Set the tab's title
        } else {
          console.error("Failed to open a new tab. Please disable your popup blocker.");
        }
      } catch (error) {
        console.error("Error opening certificate:", error);
      }
    },
    goBack() {
      this.$router.push(`/modules`);
    },
  },
  mounted() {
    this.fetchModuleDetails();
  },
};
</script>

<style scoped>
.bg-cover {
  background-size: cover;
  background-repeat: no-repeat;
}
</style>
